import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { LenderStatus } from "../../types/deals";
import { getStatusConfig, validateStatusTransition } from "../../utils/statusConfig";
import { useEffect } from "react";

// const statuses: LenderStatus[] = [
//   "Deal Shared",
//   "Credit Discussion Completed",
//   "Under Review",
//   "Unresponsive",
//   "On Hold",
//   "Term Sheet Shared",
//   "Rejected",
//   "Sanctioned",
//   "Disbursed",
// ];

const schema = z.object({
  status: z.custom<LenderStatus>(),
  note: z.string().optional(),
  reason: z.string().optional(),
  termSheetFile: z.any().optional(),
  sanctionAmount: z.coerce.number().optional(),
  sanctionLetterFile: z.any().optional(),
  // Disbursement & payouts
  payoutBasis: z.enum(["sanctioned", "disbursed", "manual"]).optional(),
  disbursedAmount: z.coerce.number().optional(),
  // amounts
  customerPayout: z.coerce.number().optional(),
  lenderPayout: z.coerce.number().optional(),
  partnerPayout: z.coerce.number().optional(),
  // percents
  customerPayoutPercent: z.coerce.number().optional(),
  lenderPayoutPercent: z.coerce.number().optional(),
  partnerPayoutPercent: z.coerce.number().optional(),

  totalRevenue: z.coerce.number().optional(),
});

export type StatusFormValues = z.infer<typeof schema>;

interface LenderStatusDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (values: StatusFormValues) => void;
  sanctionedAmountFromHistory?: number;
  lenderInstitutionName?: string;
  lenderUserName?: string;
  currentStatus: LenderStatus | '1';
  LenderStatus?: any
}

export function LenderStatusDialog({ open, onOpenChange, onSubmit, sanctionedAmountFromHistory, lenderInstitutionName, lenderUserName, currentStatus, LenderStatus }: LenderStatusDialogProps) {

  const form = useForm<StatusFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      status: String(currentStatus) as LenderStatus, // ✅ start with actual current
    },
  });
  const watchStatus = form.watch("status");

  // ✅ Reset form values when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        status: String(currentStatus) as LenderStatus,
        note: "",
        reason: "",
        termSheetFile: undefined,
        sanctionAmount: undefined,
        sanctionLetterFile: undefined,
        payoutBasis: undefined,
        disbursedAmount: undefined,
        customerPayout: undefined,
        lenderPayout: undefined,
        partnerPayout: undefined,
        customerPayoutPercent: undefined,
        lenderPayoutPercent: undefined,
        partnerPayoutPercent: undefined,
      });
    }
  }, [open, currentStatus, form]);
  
  
  const watchBasis = form.watch("payoutBasis");
  const disbursed = form.watch("disbursedAmount") ?? 0;
  const baseAmount = watchBasis === "sanctioned" ? (sanctionedAmountFromHistory ?? 0) : watchBasis === "disbursed" ? disbursed : 0;
  const cp = form.watch("customerPayoutPercent") ?? 0;
  const lp = form.watch("lenderPayoutPercent") ?? 0;
  const pp = form.watch("partnerPayoutPercent") ?? 0;
  // const statuses = LenderStatus; 


  const requiresReason = watchStatus === "5" || watchStatus === "7";
  const needsTermSheet = watchStatus === "6";
  const needsSanction = watchStatus === "8";
  const needsDisbursed = watchStatus === "9";

  useEffect(() => {
    if (watchBasis !== "manual") {
      const cp = form.watch("customerPayoutPercent") ?? 0;
      const lp = form.watch("lenderPayoutPercent") ?? 0;
      const pp = form.watch("partnerPayoutPercent") ?? 0;
  
      form.setValue("customerPayout", Number(((cp / 100) * baseAmount).toFixed(2)));
      form.setValue("lenderPayout", Number(((lp / 100) * baseAmount).toFixed(2)));
      form.setValue("partnerPayout", Number(((pp / 100) * baseAmount).toFixed(2)));
    }
  }, [
    watchBasis,
    form.watch("customerPayoutPercent"),
    form.watch("lenderPayoutPercent"),
    form.watch("partnerPayoutPercent"),
    baseAmount,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Update Status — {lenderInstitutionName ?? ""}{lenderInstitutionName ? " • " : ""}{lenderUserName ?? ""}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
        <form
  onSubmit={form.handleSubmit((vals) => {
    if (String(vals.status) === String(currentStatus)) {
      // stop submission
      form.setError("status", { type: "manual", message: "Please select a different status" });
      return;
    }
    onSubmit(vals);
  })}
  className="space-y-5"
>
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
  <FormControl>
    <SelectTrigger>
      <SelectValue placeholder="Select Status" />
    </SelectTrigger>
  </FormControl>
  <SelectContent>
    {LenderStatus.map((s: any) => {
      const config = getStatusConfig(s.id);
      const StatusIcon = config?.icon;
      const validation = validateStatusTransition(
        String(currentStatus),
        String(s.id)
      );

      const isDisabled =
        String(s.id) === String(currentStatus) || !validation.valid;

      return (
        <SelectItem
          key={s.id}
          value={String(s.id)}
          disabled={isDisabled}
          textValue={s.name}
        >
          <div className="flex items-center gap-2">
            {StatusIcon && (
              <StatusIcon
                className={`h-4 w-4 ${config?.color || "text-gray-600"}`}
              />
            )}
            {s.name}
          </div>
        </SelectItem>
      );
    })}
  </SelectContent>
</Select>
                  <FormMessage />
                </FormItem>
              )}
            />



            {requiresReason && (
              <FormField
                control={form.control}
                name="reason"
                rules={{ required: true }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reason</FormLabel>
                    <FormControl>
                      <Input placeholder="Required" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {needsTermSheet && (
              <FormField
                control={form.control}
                name="termSheetFile"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Upload Term Sheet (optional)</FormLabel>
                    <FormControl>
                      <Input type="file" onChange={(e) => field.onChange(e.target.files?.[0])} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {needsSanction && (
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name="sanctionAmount"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sanction Amount</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="Amount" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* <FormField
                  control={form.control}
                  name="sanctionLetterFile"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Upload Sanction Letter (optional)</FormLabel>
                      <FormControl>
                        <Input type="file" onChange={(e) => field.onChange(e.target.files?.[0])} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}
              </div>
            )}

            {needsDisbursed && (
              <div className="grid grid-cols-1 gap-4">
                <div className="grid grid-cols-2 gap-4">
                <FormField
                    control={form.control}
                    name="disbursedAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Disbursed Amount</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

<FormField
  control={form.control}
  name="payoutBasis"
  render={({ field }) => (
    <FormItem>
      <FormLabel>Payout Basis</FormLabel>
      <Select 
        onValueChange={(val) => field.onChange(val || undefined)} 
        value={field.value ?? undefined}   // allow undefined
      >
        <FormControl>
          <SelectTrigger>
            <SelectValue placeholder="Select basis" />
          </SelectTrigger>
        </FormControl>
        <SelectContent>
          <SelectItem value="manual">Enter Amount</SelectItem>
          <SelectItem value="sanctioned">Sanctioned</SelectItem>
          <SelectItem value="disbursed">Disbursed</SelectItem>
        </SelectContent>
      </Select>
      <FormMessage />
    </FormItem>
  )}
/>
                  
                </div>

                {typeof sanctionedAmountFromHistory === 'number' && (
                  <div className="text-xs text-muted-foreground">Sanction amount (latest): {sanctionedAmountFromHistory}</div>
                )}

{watchBasis && (
  watchBasis === "manual" ? (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <FormField
        control={form.control}
        name="customerPayout"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Customer Payout (Amount)</FormLabel>
            <FormControl>
              <Input type="number" step="0.01" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="lenderPayout"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Lender Payout (Amount)</FormLabel>
            <FormControl>
              <Input type="number" step="0.01" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="partnerPayout"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Partner Payout (Amount)</FormLabel>
            <FormControl>
              <Input type="number" step="0.01" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  ) : (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <FormField
        control={form.control}
        name="customerPayoutPercent"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Customer Payout (%)</FormLabel>
            <FormControl>
              <Input type="number" step="0.01" {...field} />
            </FormControl>
            <div className="text-xs text-muted-foreground">
              Calculated: {((Number(field.value ?? 0) / 100) * baseAmount || 0).toFixed(2)}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="lenderPayoutPercent"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Lender Payout (%)</FormLabel>
            <FormControl>
              <Input type="number" step="0.01" {...field} />
            </FormControl>
            <div className="text-xs text-muted-foreground">
              Calculated: {((Number(field.value ?? 0) / 100) * baseAmount || 0).toFixed(2)}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="partnerPayoutPercent"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Partner Payout (%)</FormLabel>
            <FormControl>
              <Input type="number" step="0.01" {...field} />
            </FormControl>
            <div className="text-xs text-muted-foreground">
              Calculated: {((Number(field.value ?? 0) / 100) * baseAmount || 0).toFixed(2)}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  )
)}

              </div>
            )}

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea rows={3} placeholder="Optional" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
